import {
  Settings,
  Users,
  CreditCard,
  Building2,
  User,
  Key,
  Server,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface SettingsSideNavProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const accountSection = {
  title: "Account",
  items: [
    {
      id: "profile",
      label: "Profile",
      icon: User,
      description: "Personal information",
    },
  ],
};

const workspaceSection = {
  title: "Workspace",
  items: [
    {
      id: "general",
      label: "General",
      icon: Settings,
      description: "Workspace name and configuration",
    },
    {
      id: "mcp-server",
      label: "MCP Server",
      icon: Server,
      description: "Manage MCP server connections",
    },
    {
      id: "api-keys",
      label: "API Keys",
      icon: Key,
      description: "API keys and provider settings",
    },
    {
      id: "members",
      label: "Members",
      icon: Users,
      description: "Manage team members and roles",
    },
    {
      id: "billing",
      label: "Billing",
      icon: CreditCard,
      description: "Billing and subscription settings",
    },
  ],
};

export function SettingsSideNav({
  activeSection,
  onSectionChange,
}: SettingsSideNavProps) {
  // Show all tabs - permissions are handled in the content
  const visibleWorkspaceItems = workspaceSection.items;

  const renderSection = (
    section: typeof accountSection | typeof workspaceSection,
    items: typeof accountSection.items,
  ) => (
    <div className="space-y-1">
      <div className="px-3 py-2">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {section.title}
        </h4>
      </div>
      {items.map((item) => {
        const Icon = item.icon;
        const isActive = activeSection === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onSectionChange(item.id)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              isActive
                ? "bg-accent text-accent-foreground font-medium"
                : "text-muted-foreground",
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <nav className="w-[200px] shrink-0 border-r border-border bg-background/50">
      <div className="p-4 space-y-6">
        {renderSection(accountSection, accountSection.items)}
        {renderSection(workspaceSection, visibleWorkspaceItems)}
      </div>
    </nav>
  );
}
